import { AlertCircle, Ban, CalendarClock, Captions, CheckCircle2, Download, FileText, FileVideo2, FileX2, FolderTree, Languages, ListOrdered, LoaderCircle, Play, RadioTower, RotateCcw, Search, Square, Trash2, Undo2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ModalPortal } from '../../components/ModalPortal'
import { FilmDetailsDialog } from '../../components/FilmDetailsDialog'
import { PageState } from '../../components/PageState'
import { SenderLogo } from '../../components/SenderLogo'
import { api, formatBytes, type ContentTag, type ContentType, type DownloadArtifact, type DownloadJob, type DownloadState, type Film, type MediaRoot, type Subscription, type SystemStatus } from '../../lib/api'
import { sourceResolution } from '../../lib/media'
import { loadLocalPreferences, saveLocalPreferences } from '../../lib/localPreferences'
import { DownloadDialog } from '../catalog/DownloadDialog'

interface Props { mediaRoots: MediaRoot[] }
type SummaryStatus = 'RUNNING' | 'QUEUED' | 'COMPLETED' | 'FAILED' | 'SKIPPED'
interface DownloadFilterPreferences {
  textFilter: string
  stateFilter: string
  sourceTypeFilter: string
  subscriptionFilter: string
  hiddenSummaryStatuses: SummaryStatus[]
}
interface DownloadProgressSample { bytes: number; receivedAt: number; bytesPerSecond: number }
interface EstimatedFileProgress { bytes: number; percent: number; bytesPerSecond: number; etaSeconds: number | null }

const DOWNLOAD_FILTERS_KEY = 'meine-mediathek.download-filters.v1'
const SUMMARY_STATUSES: SummaryStatus[] = ['RUNNING', 'QUEUED', 'COMPLETED', 'FAILED', 'SKIPPED']
const defaultHiddenSummaryStatuses = () => new Set<SummaryStatus>(['SKIPPED'])
const DOWNLOAD_FILTER_DEFAULTS: DownloadFilterPreferences = {
  textFilter: '', stateFilter: '', sourceTypeFilter: '', subscriptionFilter: '', hiddenSummaryStatuses: ['SKIPPED'],
}
function loadDownloadFilterPreferences(): DownloadFilterPreferences {
  const stored = loadLocalPreferences(DOWNLOAD_FILTERS_KEY, DOWNLOAD_FILTER_DEFAULTS)
  const stringValue = (value: unknown) => typeof value === 'string' ? value : ''
  return {
    textFilter: stringValue(stored.textFilter),
    stateFilter: stringValue(stored.stateFilter),
    sourceTypeFilter: stringValue(stored.sourceTypeFilter),
    subscriptionFilter: stringValue(stored.subscriptionFilter),
    hiddenSummaryStatuses: Array.isArray(stored.hiddenSummaryStatuses)
      ? stored.hiddenSummaryStatuses.filter((status): status is SummaryStatus => SUMMARY_STATUSES.includes(status))
      : ['SKIPPED'],
  }
}

export function DownloadsView({ mediaRoots }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const downloads = useQuery({
    queryKey: ['downloads'], queryFn: () => api<DownloadJob[]>('/api/v1/downloads?limit=1000'),
    refetchInterval: (query) => query.state.data?.some((job) => job.state === 'RUNNING') ? 1_500 : 15_000,
  })
  const subscriptions = useQuery({ queryKey: ['subscriptions'], queryFn: () => api<Subscription[]>('/api/v1/subscriptions') })
  const [initialFilters] = useState(loadDownloadFilterPreferences)
  const [textFilter, setTextFilter] = useState(initialFilters.textFilter)
  const [stateFilter, setStateFilter] = useState(initialFilters.stateFilter)
  const [sourceTypeFilter, setSourceTypeFilter] = useState(initialFilters.sourceTypeFilter)
  const [subscriptionFilter, setSubscriptionFilter] = useState(initialFilters.subscriptionFilter)
  const [hiddenSummaryStatuses, setHiddenSummaryStatuses] = useState<Set<SummaryStatus>>(
    () => new Set(initialFilters.hiddenSummaryStatuses.filter((status) => SUMMARY_STATUSES.includes(status))),
  )
  useEffect(() => {
    saveLocalPreferences(DOWNLOAD_FILTERS_KEY, { textFilter, stateFilter, sourceTypeFilter, subscriptionFilter, hiddenSummaryStatuses: [...hiddenSummaryStatuses] } satisfies DownloadFilterPreferences)
  }, [textFilter, stateFilter, sourceTypeFilter, subscriptionFilter, hiddenSummaryStatuses])
  const [deleteCandidate, setDeleteCandidate] = useState<DownloadJob | null>(null)
  const [detailFilmId, setDetailFilmId] = useState<string | null>(null)
  const [downloadFilm, setDownloadFilm] = useState<Film | null>(null)
  const detailFilm = useQuery({
    queryKey: ['catalog', 'detail', detailFilmId],
    queryFn: () => api<Film>(`/api/v1/catalog/${encodeURIComponent(detailFilmId!)}`),
    enabled: Boolean(detailFilmId),
  })
  const classification = useMutation({
    mutationFn: ({ film, contentType, tags }: { film: Film; contentType: ContentType; tags: ContentTag[] }) => api<Film>(
      `/api/v1/catalog/${encodeURIComponent(film.id)}/classification`,
      { method: 'PUT', body: JSON.stringify({ contentType, tags }) },
    ),
    onSuccess: (updated) => queryClient.setQueryData(['catalog', 'detail', updated.id], updated),
  })
  const action = useMutation({
    mutationFn: ({ id, operation }: { id: string; operation: 'start' | 'cancel' | 'skip' | 'unskip' | 'delete' | 'dismiss' }) =>
      api<void>(operation === 'delete' ? `/api/v1/downloads/${id}/files` : operation === 'dismiss' ? `/api/v1/downloads/${id}` : `/api/v1/downloads/${id}/${operation}`, {
        method: operation === 'delete' || operation === 'dismiss' ? 'DELETE' : 'POST',
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['downloads'] })
      if (variables.operation === 'delete') setDeleteCandidate(null)
    },
  })
  const bulkAction = useMutation({ mutationFn: ({ path, method = 'POST' }: { path: string; method?: 'POST' | 'DELETE' }) => api(`/api/v1/downloads/${path}`, { method }), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['downloads'] }) })
  const bulkSkip = useMutation({
    mutationFn: (downloadIds: string[]) => api('/api/v1/downloads/skip', { method: 'POST', body: JSON.stringify({ downloadIds }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['downloads'] }),
  })
  const jobs = downloads.data?.filter((job) => job.state !== 'DELETED') ?? []
  const progressSamples = useRef(new Map<string, DownloadProgressSample>())
  const [progressClock, setProgressClock] = useState(Date.now)
  useEffect(() => {
    const now = Date.now()
    const runningFileIds = new Set<string>()
    downloads.data?.forEach((job) => {
      if (job.state !== 'RUNNING') return
      if (job.processingStage == null || job.processingStage === 'VIDEO') {
        const fileId = videoProgressId(job.id)
        runningFileIds.add(fileId)
        updateProgressSample(progressSamples.current, fileId, job.bytesDownloaded, now)
      }
      job.audioTracks.filter((track) => track.state === 'DOWNLOADING').forEach((track) => {
        const fileId = audioProgressId(job.id, track.filmId)
        runningFileIds.add(fileId)
        updateProgressSample(progressSamples.current, fileId, track.bytesDownloaded, now)
      })
    })
    progressSamples.current.forEach((_sample, id) => {
      if (!runningFileIds.has(id)) progressSamples.current.delete(id)
    })
    setProgressClock(now)
  }, [downloads.data])
  const hasRunningDownload = jobs.some((job) => job.state === 'RUNNING')
  useEffect(() => {
    if (!hasRunningDownload) return
    const timer = window.setInterval(() => setProgressClock(Date.now()), 200)
    return () => window.clearInterval(timer)
  }, [hasRunningDownload])
  const counts = countStates(jobs)
  const failedCount = counts.FAILED + counts.CANCELLED + counts.UNSUPPORTED_SOURCE
  const countedJobs = jobs.length - counts.SKIPPED
  const subscriptionNames = new Map(subscriptions.data?.map((subscription) => [subscription.id, subscription.name]) ?? [])
  const normalizedTextFilter = textFilter.trim().toLocaleLowerCase('de-DE')
  const visibleJobs = jobs.filter((job) => {
    const originMatches = (!sourceTypeFilter || sourceTypeFilter === job.sourceType)
      && (!subscriptionFilter || job.subscriptionId === subscriptionFilter)
    const textMatches = !normalizedTextFilter || [job.title, job.topic, job.sender, job.targetFilename,
      job.destinationSubpath, job.mediaRootId,
      subscriptionNames.get(job.subscriptionId ?? '') ?? ''].some((value) => value.toLocaleLowerCase('de-DE').includes(normalizedTextFilter))
    const stateMatches = !hiddenSummaryStatuses.has(summaryStatus(job.state))
      && (!stateFilter || job.state === stateFilter)
    return stateMatches && originMatches && textMatches
  })
  const visibleSkippableJobs = visibleJobs.filter((job) => job.sourceType === 'SUBSCRIPTION'
    && ['QUEUED', 'FAILED', 'CANCELLED', 'UNSUPPORTED_SOURCE'].includes(job.state))
  const hasJobs = jobs.length > 0
  const summaryFiltersChanged = hiddenSummaryStatuses.size !== 1 || !hiddenSummaryStatuses.has('SKIPPED')
  const hasFilters = Boolean(textFilter || stateFilter || sourceTypeFilter || subscriptionFilter || summaryFiltersChanged)
  const toggleSummaryStatus = (status: SummaryStatus) => setHiddenSummaryStatuses((current) => {
    const next = new Set(current)
    if (next.has(status)) next.delete(status)
    else next.add(status)
    return next
  })
  const selectState = (state: string) => {
    setStateFilter(state)
    if (!state) return
    setHiddenSummaryStatuses((current) => {
      const next = new Set(current)
      next.delete(summaryStatus(state as DownloadState))
      return next
    })
  }
  const resetFilters = () => {
    setTextFilter('')
    setStateFilter('')
    setSourceTypeFilter('')
    setSubscriptionFilter('')
    setHiddenSummaryStatuses(defaultHiddenSummaryStatuses())
  }

  return <section className="page downloads-page">
    <header className="download-overview" aria-label={t('downloads.countLabel', { completed: counts.COMPLETED, total: countedJobs })}>
      <div className="status-cards compact-cards">
        <StatusCard icon={<LoaderCircle />} value={counts.RUNNING} label={t('downloads.states.RUNNING')} accent="violet" visible={!hiddenSummaryStatuses.has('RUNNING')} onClick={() => toggleSummaryStatus('RUNNING')} />
        <StatusCard icon={<ListOrdered />} value={counts.QUEUED} label={t('downloads.states.QUEUED')} accent="blue" visible={!hiddenSummaryStatuses.has('QUEUED')} onClick={() => toggleSummaryStatus('QUEUED')} />
        <StatusCard icon={<CheckCircle2 />} value={counts.COMPLETED} label={t('downloads.states.COMPLETED')} accent="green" visible={!hiddenSummaryStatuses.has('COMPLETED')} onClick={() => toggleSummaryStatus('COMPLETED')} />
        <StatusCard icon={<AlertCircle />} value={failedCount} label={t('downloads.states.FAILED')} accent="red" visible={!hiddenSummaryStatuses.has('FAILED')} onClick={() => toggleSummaryStatus('FAILED')} />
        <StatusCard icon={<Ban />} value={counts.SKIPPED} label={t('downloads.states.SKIPPED')} accent="amber" visible={!hiddenSummaryStatuses.has('SKIPPED')} onClick={() => toggleSummaryStatus('SKIPPED')} />
      </div>
    </header>
    {!downloads.isLoading && hasJobs && <div className="panel download-toolbar">
      <label className="download-search-field">{t('downloads.search')}<span><Search /><input type="text" value={textFilter} onChange={(event) => setTextFilter(event.target.value)} placeholder={t('downloads.searchPlaceholder')} />{textFilter && <button className="input-clear" aria-label={t('downloads.clearSearch')} onClick={() => setTextFilter('')}><X /></button>}</span></label>
      <label>{t('downloads.filterState')}<select value={stateFilter} onChange={(event) => selectState(event.target.value)}><option value="">{t('common.all')}</option>{(['QUEUED','RUNNING','COMPLETED','FAILED','CANCELLED','UNSUPPORTED_SOURCE','SKIPPED'] as DownloadState[]).map((state) => <option value={state} key={state}>{t(`downloads.states.${state}`)}</option>)}</select></label>
      <label>{t('downloads.filterOrigin')}<select value={sourceTypeFilter} onChange={(event) => { setSourceTypeFilter(event.target.value); if (event.target.value === 'MANUAL') setSubscriptionFilter('') }}><option value="">{t('common.all')}</option><option value="MANUAL">{t('downloads.manual')}</option><option value="SUBSCRIPTION">{t('downloads.subscription')}</option></select></label>
      <label>{t('downloads.filterSubscription')}<select value={subscriptionFilter} disabled={sourceTypeFilter === 'MANUAL'} onChange={(event) => { setSubscriptionFilter(event.target.value); if (event.target.value) setSourceTypeFilter('SUBSCRIPTION') }}><option value="">{t('downloads.allSubscriptions')}</option>{subscriptions.data?.map((subscription) => <option value={subscription.id} key={subscription.id}>{subscription.name}</option>)}</select></label>
      <div className="download-toolbar-actions"><button className="quiet-button" disabled={!counts.QUEUED} onClick={() => bulkAction.mutate({ path: 'start-queued' })}><Play />{t('downloads.startQueued')}</button><button className="quiet-button" disabled={!counts.QUEUED && !counts.RUNNING} onClick={() => bulkAction.mutate({ path: 'cancel-active' })}><Square />{t('downloads.cancelActive')}</button><button className="quiet-button skip-button" disabled={!hasFilters || visibleSkippableJobs.length === 0 || bulkSkip.isPending} onClick={() => window.confirm(t('downloads.confirmSkipFiltered', { count: visibleSkippableJobs.length })) && bulkSkip.mutate(visibleSkippableJobs.map((job) => job.id))}><Ban />{t('downloads.skipFiltered', { count: visibleSkippableJobs.length })}</button><button className="quiet-button" disabled={!counts.COMPLETED} onClick={() => window.confirm(t('downloads.confirmDismissCompleted')) && bulkAction.mutate({ path: 'completed', method: 'DELETE' })}><X />{t('downloads.dismissCompleted')}</button></div>
    </div>}
    <div className={`panel download-list ${downloads.isLoading || visibleJobs.length === 0 ? 'page-state-host' : ''}`}>
      {downloads.isLoading && <PageState icon={<LoaderCircle className="spin" />} title={t('downloads.loadingTitle')} description={t('downloads.loadingHint')} />}
      {!downloads.isLoading && visibleJobs.length === 0 && <PageState
        icon={<Download />}
        title={t(hasJobs ? 'downloads.filteredEmptyTitle' : 'downloads.emptyTitle')}
        description={t(hasJobs ? 'downloads.filteredEmptyHint' : 'downloads.emptyHint')}
      >
        {hasJobs
          ? <button className="quiet-button" onClick={resetFilters}><X />{t('downloads.resetFilters')}</button>
          : <Link className="primary-button" to="/sendungen"><Download />{t('downloads.findPrograms')}</Link>}
      </PageState>}
      {visibleJobs.map((job) => {
        const progress = estimatedDownloadProgress(job, progressClock, progressSamples.current.get(videoProgressId(job.id)))
        const audioProgress = new Map(job.audioTracks.map((track) => [
          track.filmId,
          estimatedAudioProgress(track, progressClock, progressSamples.current.get(audioProgressId(job.id, track.filmId))),
        ]))
        const compoundProgress = compoundDownloadProgress(job, progress.bytes)
        const resolution = sourceResolution(job.selectedUrl)
        return <article
        className={`download-row ${job.state === 'SKIPPED' ? 'is-skipped' : ''}`}
        key={job.id}
        role="button"
        tabIndex={0}
        aria-label={t('downloads.openDetails', { title: job.title })}
        onClick={() => setDetailFilmId(job.filmId)}
        onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); setDetailFilmId(job.filmId) } }}
      >
        <StateIcon job={job} />
        <div className="download-main">
          <div className={`download-origin-heading ${job.sourceType === 'SUBSCRIPTION' ? 'subscription' : 'manual'}`}>{job.sourceType === 'SUBSCRIPTION' ? <RadioTower /> : <Download />}<strong>{job.sourceType === 'SUBSCRIPTION' ? t('downloads.originSubscription', { name: subscriptionNames.get(job.subscriptionId ?? '') ?? t('downloads.deletedSubscription') }) : t('downloads.originManual')}</strong></div>
          <div className="download-title"><SenderLogo sender={job.sender} compact /><strong>{job.title}</strong>{(job.audioTracks.length > 0 || job.subtitlesEnabled) && <span className="download-feature-badges">{job.audioTracks.map((track) => <span key={track.filmId} title={t('sendungen.includesAudioLanguage', { language: track.label })}><Languages />+ {track.label}</span>)}{job.subtitlesEnabled && <span><Captions />{t('downloads.subtitleBadge')}</span>}</span>}</div>
          <DownloadFiles job={job} videoProgress={progress} audioProgress={audioProgress} />
          <div className="download-meta">
            <span className={job.state === 'RUNNING' ? 'download-processing-status' : undefined}>{job.state === 'RUNNING' && <LoaderCircle className="spin" />}{job.state === 'RUNNING' ? processingStageLabel(job, t) : job.state === 'QUEUED' && !job.startAutomatically ? t('downloads.scheduled') : t(`downloads.states.${job.state}`)}</span>
            <span>{formatBytes(compoundProgress.bytes)}{compoundProgress.total ? ` / ${formatBytes(compoundProgress.total)}` : ''}</span>
            <span>{t(`downloadDialog.${job.quality.toLowerCase()}`)}{resolution ? ` · ${resolution}` : ''}</span>
            <span>{new Date(job.createdAt).toLocaleString('de-DE')}</span>
            {job.retryCount > 0 && <span>{t('downloads.retryCount', { count: job.retryCount })}</span>}
            {job.errorDetail && <span className="error-text">{job.errorDetail}</span>}
            {job.errorDetail && <span className="download-failed-source"><b>{t('downloads.failedSource')}</b><code title={job.selectedUrl}>{job.selectedUrl}</code></span>}
          </div>
        </div>
        <div className="row-actions" onClick={(event) => event.stopPropagation()}>
          {(job.state === 'QUEUED' && !job.startAutomatically) && <button className="icon-button" title={t('common.start')} onClick={() => action.mutate({ id: job.id, operation: 'start' })}><Play /></button>}
          {(job.state === 'FAILED' || job.state === 'CANCELLED' || job.state === 'UNSUPPORTED_SOURCE') && <button className="icon-button" title={t('downloads.retry')} onClick={() => action.mutate({ id: job.id, operation: 'start' })}><RotateCcw /></button>}
          {(job.state === 'RUNNING' || job.state === 'QUEUED') && <button className="icon-button danger" title={t('downloads.cancel')} onClick={() => action.mutate({ id: job.id, operation: 'cancel' })}><Square /></button>}
          {job.sourceType === 'SUBSCRIPTION' && ['QUEUED','FAILED','CANCELLED','UNSUPPORTED_SOURCE'].includes(job.state) && <button className="icon-button skip" title={t('downloads.skipEpisode')} onClick={() => window.confirm(t('downloads.confirmSkipEpisode', { title: job.title })) && action.mutate({ id: job.id, operation: 'skip' })}><Ban /></button>}
          {job.state === 'SKIPPED' && <button className="icon-button" title={t('downloads.unskipEpisode')} onClick={() => action.mutate({ id: job.id, operation: 'unskip' })}><Undo2 /></button>}
          {job.state === 'COMPLETED' && <button className="icon-button danger" title={t('downloads.deleteFiles')} onClick={() => { action.reset(); setDeleteCandidate(job) }}><Trash2 /></button>}
          {['COMPLETED','FAILED','CANCELLED','UNSUPPORTED_SOURCE'].includes(job.state) && <button className="icon-button" title={t('downloads.dismiss')} onClick={() => action.mutate({ id: job.id, operation: 'dismiss' })}><X /></button>}
        </div>
      </article>})}
    </div>
    {deleteCandidate && <DeleteFilesDialog job={deleteCandidate} pending={action.isPending} error={action.error instanceof Error ? action.error.message : undefined} onClose={() => setDeleteCandidate(null)} onConfirm={() => action.mutate({ id: deleteCandidate.id, operation: 'delete' })} />}
    {detailFilmId && detailFilm.data && <FilmDetailsDialog
      film={detailFilm.data}
      libraryStatus={detailFilm.data.library}
      classificationUpdating={classification.isPending}
      classificationError={classification.isError}
      onClassificationChange={(contentType, tags) => classification.mutate({ film: detailFilm.data!, contentType, tags })}
      onClose={() => setDetailFilmId(null)}
      onDownload={() => { setDownloadFilm(detailFilm.data!); setDetailFilmId(null) }}
    />}
    {downloadFilm && <DownloadDialog film={downloadFilm} mediaRoots={mediaRoots} onClose={() => setDownloadFilm(null)} />}
  </section>
}

function DownloadFiles({ job, videoProgress, audioProgress }: { job: DownloadJob; videoProgress: EstimatedFileProgress; audioProgress: Map<string, EstimatedFileProgress> }) {
  const { t } = useTranslation()
  const folder = downloadFolder(job)
  const stem = job.targetFilename.replace(/\.[^.]+$/, '')
  return <div className="download-files" aria-label={t('downloads.files')}>
    <span className="download-folder" title={folder}><FolderTree /><code>{folder}/</code></span>
    <span className="download-file-list">
      <DownloadFile icon={<FileVideo2 />} filename={job.targetFilename} state={videoFileState(job)} details={fileProgressDetails(videoProgress, job.totalBytes, t)} progressPercent={videoFileProgressPercent(job, videoProgress.percent)} indeterminate={job.state === 'RUNNING' && (job.processingStage == null || job.processingStage === 'VIDEO') && !job.totalBytes} />
      {job.audioTracks.map((track) => {
        const progress = audioProgress.get(track.filmId) ?? estimatedAudioProgress(track, Date.now())
        return <DownloadFile key={track.filmId} icon={<Languages />} filename={track.filename} state={audioFileState(track.state)} status={t(`downloads.audioFileStates.${track.state}`)} details={audioFileProgressDetails(track, progress, t)} progressPercent={audioFileProgressPercent(track, progress.percent)} indeterminate={track.state === 'PREPARING' || track.state === 'READY' || track.state === 'DOWNLOADING' && !track.totalBytes || track.state === 'EXTRACTING' && track.processingProgressPercent <= 0} error={track.errorDetail} />
      })}
      {job.subtitlesEnabled && <DownloadFile icon={<Captions />} filename={`${stem}.[vtt|ttml]`} state={sidecarFileState(job.subtitleState)} progressPercent={sidecarProgressPercent(job.subtitleState)} indeterminate={job.subtitleState === 'RUNNING'} />}
      {job.infoFileEnabled && <DownloadFile icon={<FileText />} filename={`${stem}.txt`} state={sidecarFileState(job.infoFileState)} progressPercent={sidecarProgressPercent(job.infoFileState)} indeterminate={job.infoFileState === 'RUNNING'} />}
    </span>
  </div>
}

function DownloadFile({ icon, filename, state, status: statusOverride, details, progressPercent, indeterminate, error }: { icon: React.ReactNode; filename: string; state: FileDisplayState; status?: string; details?: string; progressPercent?: number; indeterminate?: boolean; error?: string | null }) {
  const { t } = useTranslation()
  const status = statusOverride ?? t(`downloads.fileStates.${state}`)
  return <span className={`download-file status-${state.toLowerCase()}`} title={error || `${filename} · ${status}`}>
    <span className="download-file-name">{icon}<code>{filename}</code></span>
    <span className={`download-file-progress ${indeterminate ? 'is-indeterminate' : ''}`}><i style={indeterminate ? undefined : { width: `${progressPercent ?? 0}%` }} /></span>
    <span className="download-file-details">{details}</span>
    <small>{status}</small>
  </span>
}

type FileDisplayState = 'WAITING' | 'RUNNING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

function videoFileState(job: DownloadJob): FileDisplayState {
  if (job.state === 'COMPLETED' || job.processingStage && job.processingStage !== 'VIDEO'
      || job.errorCode === 'COMPOUND_DOWNLOAD_INCOMPLETE') return 'COMPLETED'
  if (job.state === 'RUNNING') return 'RUNNING'
  if (job.state === 'FAILED' || job.state === 'UNSUPPORTED_SOURCE') return 'FAILED'
  if (job.state === 'CANCELLED' || job.state === 'SKIPPED' || job.state === 'DELETED') return 'CANCELLED'
  return 'WAITING'
}

function audioFileState(state: DownloadJob['audioTracks'][number]['state']): FileDisplayState {
  if (state === 'DOWNLOADING') return 'RUNNING'
  if (state === 'PREPARING' || state === 'EXTRACTING' || state === 'READY') return 'PROCESSING'
  if (state === 'COMPLETED') return 'COMPLETED'
  if (state === 'FAILED') return 'FAILED'
  if (state === 'CANCELLED') return 'CANCELLED'
  return 'WAITING'
}

function sidecarFileState(state: DownloadJob['subtitleState']): FileDisplayState {
  if (state === 'RUNNING') return 'RUNNING'
  if (state === 'COMPLETED') return 'COMPLETED'
  if (state === 'FAILED') return 'FAILED'
  if (state === 'CANCELLED') return 'CANCELLED'
  return 'WAITING'
}

function videoFileProgressPercent(job: DownloadJob, estimatedPercent: number) {
  if (videoFileState(job) === 'COMPLETED') return 100
  return Math.max(0, Math.min(100, estimatedPercent))
}

function audioFileProgressPercent(track: DownloadJob['audioTracks'][number], estimatedPercent: number) {
  if (track.state === 'READY' || track.state === 'COMPLETED') return 100
  if (track.state === 'EXTRACTING') return Math.max(0, Math.min(100, track.processingProgressPercent))
  if (track.state === 'DOWNLOADING') return Math.max(0, Math.min(100, estimatedPercent))
  return 0
}

function sidecarProgressPercent(state: DownloadJob['subtitleState']) {
  return state === 'COMPLETED' ? 100 : 0
}

function fileProgressDetails(progress: EstimatedFileProgress, total: number | null, t: (key: string, options?: Record<string, unknown>) => string) {
  const transferred = total && total > 0
    ? `${formatBytes(Math.min(total, progress.bytes))} / ${formatBytes(total)}`
    : progress.bytes > 0 ? formatBytes(progress.bytes) : ''
  const rate = progress.bytesPerSecond > 0
    ? progress.etaSeconds == null
      ? t('downloads.speed', { speed: formatBytes(progress.bytesPerSecond) })
      : t('downloads.speedEta', { speed: formatBytes(progress.bytesPerSecond), eta: formatRemainingTime(progress.etaSeconds) })
    : ''
  return [transferred, rate].filter(Boolean).join(' · ')
}

function audioFileProgressDetails(track: DownloadJob['audioTracks'][number], progress: EstimatedFileProgress, t: (key: string, options?: Record<string, unknown>) => string) {
  if (track.state === 'DOWNLOADING') {
    const transfer = fileProgressDetails(progress, track.totalBytes, t)
    return transfer ? t('downloads.audioSourceProgress', { progress: transfer }) : ''
  }
  if (track.state === 'EXTRACTING' && track.processingProgressPercent > 0) {
    return t('downloads.extractionProgress', { percent: Math.round(track.processingProgressPercent) })
  }
  return ''
}

function compoundDownloadProgress(job: DownloadJob, estimatedVideoBytes: number) {
  const videoTotal = job.totalBytes && job.totalBytes > 0 ? job.totalBytes : null
  const audioTracks = job.audioTracks.filter((track) => track.totalBytes && track.totalBytes > 0)
  if (!videoTotal || audioTracks.length === 0) {
    return { bytes: estimatedVideoBytes, total: videoTotal }
  }
  let bytes = Math.min(videoTotal, Math.max(0, estimatedVideoBytes))
  let total = videoTotal
  for (const track of audioTracks) {
    const trackTotal = track.totalBytes!
    total += trackTotal
    bytes += track.state === 'COMPLETED' ? trackTotal : Math.min(trackTotal, Math.max(0, track.bytesDownloaded))
  }
  return { bytes, total }
}

function processingStageLabel(job: DownloadJob, t: (key: string, options?: Record<string, unknown>) => string) {
  const stage = job.processingStage ?? 'VIDEO'
  if (stage === 'VIDEO' && job.audioTracks.length === 0) return t('downloads.processingStages.VIDEO_ONLY')
  return t(`downloads.processingStages.${stage}`, { count: job.audioTracks.length })
}

function downloadFolder(job: DownloadJob) {
  return [job.mediaRootId, job.destinationSubpath]
    .filter((part) => Boolean(part))
    .join('/')
    .replaceAll('\\', '/')
    .replaceAll(/\/{2,}/g, '/')
}

function DeleteFilesDialog({ job, pending, error, onClose, onConfirm }: { job: DownloadJob; pending: boolean; error?: string; onClose: () => void; onConfirm: () => void }) {
  const { t } = useTranslation()
  const artifacts = useQuery({ queryKey: ['download-artifacts', job.id], queryFn: () => api<DownloadArtifact[]>(`/api/v1/downloads/${job.id}/artifacts`) })
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api<SystemStatus>('/api/v1/system/status'),
    staleTime: 30_000,
  })
  const rootPaths = new Map(status.data?.mediaRoots.map((root) => [root.id, root.path]) ?? [])
  return <ModalPortal><div className="modal-backdrop" onMouseDown={onClose}>
    <section className="modal-card delete-files-modal" role="dialog" aria-modal="true" aria-labelledby="delete-files-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="modal-header"><div><span className="eyebrow"><Trash2 />{t('downloads.deleteFiles')}</span><h2 id="delete-files-title">{t('downloads.deleteTitle')}</h2></div><button className="icon-button" aria-label={t('common.close')} onClick={onClose}><X /></button></header>
      <div className="delete-files-job"><strong>{job.title}</strong><span>{job.topic}</span></div>
      <p className="delete-files-warning">{t('downloads.deleteWarning')}</p>
      <div className="delete-artifact-list">
        {artifacts.isLoading && <div className="table-loading">{t('common.loading')}</div>}
        {artifacts.data?.map((artifact) => {
          const rootPath = rootPaths.get(artifact.mediaRootId)
          const fullPath = rootPath ? joinPath(rootPath, artifact.relativePath) : `${artifact.mediaRootId}/${artifact.relativePath}`
          return <div className="delete-artifact" key={artifact.id}>
            <FileX2 />
            <div><span>{t(`downloads.artifactTypes.${artifact.type}`)}</span><code>{fullPath}</code><small>{formatBytes(artifact.sizeBytes)}</small></div>
          </div>
        })}
        {!artifacts.isLoading && artifacts.data?.length === 0 && <div className="empty-artifacts">{t('downloads.noArtifacts')}</div>}
      </div>
      {error && <div className="inline-error">{error}</div>}
      <footer className="modal-actions"><button className="quiet-button" disabled={pending} onClick={onClose}>{t('common.cancel')}</button><button className="primary-button danger-button" disabled={pending || artifacts.isLoading} onClick={onConfirm}><Trash2 />{t('downloads.confirmDeleteButton', { count: artifacts.data?.length ?? 0 })}</button></footer>
    </section>
  </div></ModalPortal>
}

function StateIcon({ job }: { job: DownloadJob }) {
  const { t } = useTranslation()
  const label = job.state === 'QUEUED' && !job.startAutomatically
    ? t('downloads.scheduledHint')
    : job.state === 'QUEUED' ? t('downloads.queuedHint') : t(`downloads.states.${job.state}`)
  if (job.state === 'QUEUED') return <div className={`state-icon ${job.startAutomatically ? 'queued' : 'scheduled'}`} title={label} aria-label={label}>{job.startAutomatically ? <ListOrdered /> : <CalendarClock />}</div>
  if (job.state === 'RUNNING') return <div className="state-icon running" title={label} aria-label={label}><LoaderCircle /></div>
  if (job.state === 'COMPLETED') return <div className="state-icon completed" title={label} aria-label={label}><CheckCircle2 /></div>
  if (job.state === 'FAILED' || job.state === 'UNSUPPORTED_SOURCE') return <div className="state-icon failed" title={label} aria-label={label}><AlertCircle /></div>
  if (job.state === 'CANCELLED') return <div className="state-icon cancelled" title={label} aria-label={label}><X /></div>
  if (job.state === 'SKIPPED') return <div className="state-icon skipped" title={label} aria-label={label}><Ban /></div>
  return <div className="state-icon deleted" title={label} aria-label={label}><FileX2 /></div>
}

function StatusCard({ icon, value, label, accent, visible, onClick }: { icon: React.ReactNode; value: number; label: string; accent: string; visible: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  const actionLabel = t(visible ? 'downloads.hideStatus' : 'downloads.showStatus', { state: label })
  return <button className={`status-card ${accent} interactive ${visible ? '' : 'is-hidden'}`} aria-pressed={visible} title={actionLabel} onClick={onClick}>{icon}<span><strong>{value}</strong><span>{label}</span></span></button>
}

function summaryStatus(state: DownloadState): SummaryStatus {
  if (state === 'FAILED' || state === 'CANCELLED' || state === 'UNSUPPORTED_SOURCE') return 'FAILED'
  if (state === 'DELETED') return 'COMPLETED'
  return state
}

function countStates(jobs: DownloadJob[]) {
  return jobs.reduce<Record<DownloadState, number>>((result, job) => {
    result[job.state]++
    return result
  }, { QUEUED: 0, RUNNING: 0, COMPLETED: 0, FAILED: 0, CANCELLED: 0, UNSUPPORTED_SOURCE: 0, SKIPPED: 0, DELETED: 0 })
}

function videoProgressId(jobId: string) {
  return `${jobId}:video`
}

function audioProgressId(jobId: string, filmId: string) {
  return `${jobId}:audio:${filmId}`
}

function updateProgressSample(samples: Map<string, DownloadProgressSample>, id: string, bytes: number, now: number) {
  const previous = samples.get(id)
  if (!previous || bytes < previous.bytes) {
    samples.set(id, { bytes, receivedAt: now, bytesPerSecond: 0 })
    return
  }
  if (bytes <= previous.bytes) return
  const elapsedSeconds = Math.max(.05, (now - previous.receivedAt) / 1000)
  const measuredSpeed = (bytes - previous.bytes) / elapsedSeconds
  const bytesPerSecond = previous.bytesPerSecond > 0
    ? previous.bytesPerSecond * .55 + measuredSpeed * .45
    : measuredSpeed
  samples.set(id, { bytes, receivedAt: now, bytesPerSecond })
}

function estimatedDownloadProgress(job: DownloadJob, now: number, sample?: DownloadProgressSample): EstimatedFileProgress {
  const activelyDownloading = job.state === 'RUNNING'
    && (job.processingStage == null || job.processingStage === 'VIDEO')
  return estimatedFileProgress(job.bytesDownloaded, job.totalBytes, job.progressPercent,
    activelyDownloading, now, sample)
}

function estimatedAudioProgress(track: DownloadJob['audioTracks'][number], now: number, sample?: DownloadProgressSample): EstimatedFileProgress {
  const reportedPercent = track.totalBytes && track.totalBytes > 0
    ? track.bytesDownloaded / track.totalBytes * 100
    : 0
  return estimatedFileProgress(track.bytesDownloaded, track.totalBytes, reportedPercent,
    track.state === 'DOWNLOADING', now, sample)
}

function estimatedFileProgress(bytesDownloaded: number, totalBytes: number | null, reportedPercent: number,
                               activelyDownloading: boolean, now: number,
                               sample?: DownloadProgressSample): EstimatedFileProgress {
  const sampleAgeSeconds = sample ? Math.max(0, (now - sample.receivedAt) / 1000) : Number.POSITIVE_INFINITY
  const bytesPerSecond = activelyDownloading && sampleAgeSeconds <= 2.5 ? sample?.bytesPerSecond ?? 0 : 0
  let bytes = bytesDownloaded
  if (bytesPerSecond > 0 && sample) {
    bytes = Math.max(bytes, sample.bytes + bytesPerSecond * sampleAgeSeconds)
  }
  if (totalBytes && totalBytes > 0) {
    const ceiling = Math.max(bytesDownloaded, totalBytes * .995)
    bytes = Math.min(ceiling, bytes)
  }
  const percent = totalBytes && totalBytes > 0
    ? Math.min(100, Math.max(reportedPercent, bytes / totalBytes * 100))
    : reportedPercent
  const etaSeconds = bytesPerSecond > 0 && totalBytes && totalBytes > bytes
    ? (totalBytes - bytes) / bytesPerSecond
    : null
  return { bytes: Math.round(bytes), percent, bytesPerSecond, etaSeconds }
}

function formatRemainingTime(seconds: number) {
  if (seconds < 60) return `${Math.max(1, Math.ceil(seconds))} s`
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} min`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.ceil((seconds % 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, '0')} h`
}

function joinPath(root: string, relative: string) {
  return `${root.replace(/[\\/]+$/, '')}/${relative.replace(/^[\\/]+/, '')}`.replaceAll('\\', '/')
}
